import React, { useMemo, useState } from "react";
import TransitionOnLoad from "Components/TransitionOnLoad";
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Row,
} from "reactstrap";
import AbsentDatePicker from "Pages/Melder/Substitutions/Overview/AbsentDatePicker";
import SchoolPlanningTable from "Pages/Melder/Substitutions/Overview/SchoolPlanningTable";
import CentralPlanningTable from "Pages/Melder/Substitutions/Overview/CentralPlanningTable";
import BehandeldePlanningTable from "Pages/Melder/Substitutions/Overview/BehandeldePlanningTable";
import AbsentFilter from "../AbsentFilter";
import { convertHeaders } from "Pages/Melder/Substitutions/Overview/AbsentTableUtils";
import SubstituterSearch from "../../../../../Components/Search/SubRequest";
import Loader from "react-loaders";
import { Link } from "react-router-dom";
import moment from "moment";
import AbsentReactTable from "./AbsentReactTable";
import Tippy from "@tippyjs/react";
import { HiOutlineDotsCircleHorizontal } from "react-icons/hi";
import { BsCheckCircle } from "react-icons/bs";
import Historie from "../../../../../assets/utils/images/svgs/Historie.svg";
import TextLimit from "Api/TextLimit";
import { useAbsentDashboard } from "Api/Melder/Substitution/Overview";


const dataNullObject = {
  schools: [],
  central_schedule: [],
  school_schedule: [],
  handled_requests: [],
  absents: {
    headerDates: [],
    data: [],
  },
};

const AbsentDashboard = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [school, setSchool] = useState(null);

  const formattedDate = useMemo(
    () => currentDate.toLocaleDateString("es-CL"),
    [currentDate]
  );

  const requestParams = useMemo(() => {
    const params = {
      "start-date": formattedDate,
    };

    if (school) {
      params.school = school;
    }

    return params;
  }, [formattedDate, school]);

  const { data: response, isLoading } = useAbsentDashboard(requestParams, {
    revalidateOnFocus: false,
  });

  const data = response || dataNullObject;
  const schools = data?.schools?.data || [];
  const absentErrorMessage = data?.absents?.errorMessage || "";
  const rawAbsentData = data?.absents?.data?.data;

  const absentDataRows = useMemo(() => {
    if (Array.isArray(rawAbsentData)) return rawAbsentData;
    if (rawAbsentData && typeof rawAbsentData === "object") {
      return Object.keys(rawAbsentData)
        .filter((key) => key !== "length")
        .map((key) => rawAbsentData[key]);
    }
    return [];
  }, [rawAbsentData]);
  const absentSchools = useMemo(() => schools, [schools]);

  const handleCallback = (schoolId) => {
    setSchool(schoolId || null);
  };


  const absentHeaders = useMemo(() => {
    const headerDates = data?.absents?.data?.headerDates || [];

    if (!headerDates.length) return [];

    const headers = JSON.parse(JSON.stringify(headerDates));

    headers.forEach((item) => {
      if (item.date) {
        item.date =
          new Date(
            moment(item.date, "DD-MM-YYYY").toDate()
          ).toLocaleDateString("nl-NL", {
            weekday: "short",
          }) +
          moment(item.date, "DD-MM-YYYY").format(" DD-MM-YYYY");
      }
    });

    return convertHeaders(headers);
  }, [data]);

  const absentCols = useMemo(() => absentHeaders, [absentHeaders]);

  const iconSwitch = (param) => {
    switch (param) {
      case "Openstaand":
        return (
          <Tippy content={param} placement="bottom">
            <span>
              <HiOutlineDotsCircleHorizontal fontSize={"24px"} className="othersSVGs" />
            </span>
          </Tippy>
        );

      case "Afgesloten":
        return (
          <Tippy content={param} placement="bottom">
            <span>
              <img src={Historie} width="24" alt="Afgesloten" />
            </span>
          </Tippy>
        );

      case "Behandeld":
        return (
          <Tippy content={param} placement="bottom">
            <span>
              <BsCheckCircle color="#39A825" fontSize={"21px"} />
            </span>
          </Tippy>
        );

      default:
        return "--";
    }
  };

  const planingColumns = useMemo(
    () => [
      {
        header: "Id",
        accessorKey: "iSubstitutionId",
        cell: ({ row }) => (
          <Link to={`/verzoeken/${row.original.iSubstitutionId}`}>
            {row.original.iSubstitutionId}
          </Link>
        ),
      },
      {
        header: "Datum",
        accessorKey: "dAddedByDir",
      },
      {
        header: "School",
        accessorKey: "vSchoolName",
        cell: ({ row }) =>
          window.innerWidth > 1250
            ? TextLimit.limitStringLargeScreen(row.original.vSchoolName)
            : TextLimit.limitStringSmallScreen(row.original.vSchoolName),
      },
      {
        header: "Afwezige",
        accessorKey: "vFirstName",
        cell: ({ row }) =>
          window.innerWidth > 1250
            ? TextLimit.AfwezigeLimitOptionOne(
              row.original.vFirstName,
              row.original.vMiddleName,
              row.original.vLastName
            )
            : TextLimit.AfwezigeLimitOptionTwo(
              row.original.vFirstName,
              row.original.vMiddleName,
              row.original.vLastName
            ),
      },
      {
        header: "1e dag",
        accessorKey: "vSubsDate",
      },
      {
        header: "1e niet ingepland",
        accessorKey: "vFirstDateNotPlanned",
      },
      {
        header: "Laatste dag",
        accessorKey: "vLastDate",
      },
      {
        header: "Code",
        accessorKey: "vCode",
        cell: ({ row }) => TextLimit.codeLimitOptionOne(row.original.vCode),
      },
      {
        header: "Status",
        accessorKey: "eStatus",
        cell: ({ row }) => iconSwitch(row.original.eStatus),
      },
    ],
    []
  );

  return (
    <div>
      <TransitionOnLoad>
        <div>
          <Row className="tableMainRow">
            <Col md="12" lg="12">
              <Card className="main-card mb-3">
                <CardHeader className="row m-0">
                  <div className="col-md-3">
                    <div className="row">
                      <span className="col-md-9 ps-0 head-text-color menu-header-title planbord">
                        <span className="text-planbord">Planbord</span>
                        <span className="planbord-count">
                          ({absentDataRows.length})
                        </span>
                      </span>
                    </div>
                  </div>

                  <AbsentDatePicker
                    currentDate={currentDate}
                    setCurrentDate={setCurrentDate}
                  />
                </CardHeader>

                <CardBody className="p-3 d-flex justify-content-center text-capitalize">
                  {isLoading ? (
                    <Loader type="ball-grid-pulse" />
                  ) : absentErrorMessage ? (
                    <div>{absentErrorMessage}</div>
                  ) : (
                    <AbsentReactTable
                      data={absentDataRows}
                      columns={absentCols}
                      filters={{
                        search: false,
                        pagination: false,
                        sorting: false,
                      }}
                      school={absentSchools}
                      tableClassName="absent-table-dashboard mb-0 table table-borderless table-sm subs-main-table"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>

            <Col md="12">
              <SchoolPlanningTable
                columns={planingColumns}
                name="School planning"
                data={data}
                filters={{
                  search: false,
                  pagination: false,
                  sorting: true,
                }}
              />
            </Col>

            <Col md="12">
              <CentralPlanningTable
                columns={planingColumns}
                name="Centrale planning"
                data={data}
                filters={{
                  search: false,
                  pagination: true,
                  sorting: true,
                }}
              />
            </Col>

            <Col md="12">
              <BehandeldePlanningTable
                columns={planingColumns}
                name="Behandelde verzoeken"
                data={data}
                filters={{
                  search: false,
                  pagination: true,
                  sorting: true,
                }}
              />
            </Col>
          </Row>
        </div>
      </TransitionOnLoad>


      <div className="chat-layout-footer verzoeken-footer-height-melder">
        <CardFooter className="d-block d-flex chat-layout-footer__inner card-footer-melder">
          <AbsentFilter schoolsListing={{ schools: absentSchools }} handleCallback={handleCallback} selectedSchool={school} />
          <SubstituterSearch />
        </CardFooter>
      </div>
    </div>
  );
};

export default AbsentDashboard;

